import React from 'react';

import {
  SubsectionForm,
  FormInput,
  FormDropdown,
  NumberInput,
  FormFieldStack,
} from '../../../../components/forms';
import { requestStepFieldModeProps } from '../../../../components/forms/viewOnlyFieldUtils';
import { PermissionAwareField } from '../../../../components/PermissionAwareField';
import { FACILITY_POWER_SECTION_SUBTITLE } from '../../../../constants/facilityPowerSectionSubtitles';
import { FACILITY_POWER_SUBSECTION_FIELDS } from '../../../../constants/requestFieldDescriptors';
import { SECTION_IDS } from '../../../../constants/sectionIds';
import { useUOM } from '../../../../contexts/UOMContext';
import { useFormFieldVisibilityContext } from '../../../../hooks/useFormFieldVisibilityContext';
import {
  resolveFacilityPowerVisibleSections,
  shouldShowPowerDistributionSection,
} from '../../../../utils/facilityPowerScopeSections';
import { isFormFieldKeyVisibleInView } from '../../../../utils/formFieldVisibility';
import { subsectionHeaderMetricsProps } from '../../../../utils/subsectionHeaderMetrics';
import { LargeUpsInformation } from '../../largeups/subsections/LargeUpsInformation';
import { PowerDistributionInformation } from '../../powerdistribution/subsections/PowerDistributionInformation';

import type { SelectedFile } from '../../../../components/forms/FileUpload';
import type { SavedAttachment } from '../../../../services/attachmentService';
import type { Request } from '../../../../types';
import type { FormFieldVisibilityContext } from '../../../../utils/formFieldVisibility';

type FacilityPowerReadinessAttachmentBundle = {
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  /** When true, main Facility Power card is omitted (multi-instance list owns it). */
  omitMainFacilityPowerCard?: boolean;
};

export type FacilityPowerReadinessProps =
  | ({
      mode: 'initial';
      formData: Record<string, unknown> & {
        stage?: string;
        sysRequestStage?: string;
      };
      updateFormData: (field: string, value: string) => void;
      requestId?: string;
      disabled?: boolean;
      isEditing?: boolean;
      showFullDetails?: boolean;
      enabledStepIds?: readonly string[];
      storedStepIds?: string[] | null;
    } & FacilityPowerReadinessAttachmentBundle)
  | ({
      mode: 'persisted';
      request: Request;
      isEditing: boolean;
      editedData: Record<string, unknown>;
      updateEditedData: (field: string, value: string) => void;
      showFullDetails?: boolean;
      enabledStepIds?: readonly string[];
      storedStepIds?: string[] | null;
    } & FacilityPowerReadinessAttachmentBundle);

const voltageOptions = ['208', '230', '240', '380', '400', '415', '480', '600'];

function FacilityPowerReadinessPermissionField({
  fieldId,
  requestStage,
  requestId,
  showFullDetails,
  formDisabled,
  fieldVisibility,
  children,
}: {
  fieldId: string;
  requestStage?: string;
  requestId?: string;
  showFullDetails?: boolean;
  formDisabled: boolean;
  fieldVisibility: FormFieldVisibilityContext;
  children: React.ReactNode;
}) {
  if (!isFormFieldKeyVisibleInView(fieldId, fieldVisibility)) {
    return null;
  }
  // Step edit mode: list-level gate already enforced; field RBAC R caused fake disabled chrome (#997).
  if (!requestStage || !formDisabled) {
    return <>{children}</>;
  }
  return (
    <PermissionAwareField
      fieldId={fieldId}
      formSection="Facility Power Readiness"
      requestStage={requestStage}
      requestId={requestId}
      showFullDetails={showFullDetails}
    >
      {children}
    </PermissionAwareField>
  );
}

/**
 * Facility Power readiness card (create + opened-request).
 * Legacy requests may also fold Large UPS / Power Distribution inline via sibling subsection imports.
 */
export const FacilityPowerReadiness: React.FC<
  FacilityPowerReadinessProps
> = props => {
  const {
    attachmentsBySection = {},
    onAttachFiles,
    onDeleteAttachment,
    selectedFiles = {},
    onFilesSelected,
    offlineMode = false,
    onCaptionChange,
    pendingCaptionEdits = {},
    omitMainFacilityPowerCard = false,
    enabledStepIds,
    storedStepIds,
  } = props;

  const visibleSections = resolveFacilityPowerVisibleSections({
    enabledStepIds,
    storedStepIds,
  });

  const showFullDetails =
    props.mode === 'initial'
      ? props.showFullDetails ?? false
      : props.showFullDetails ?? true;
  const requestStageForVisibility =
    props.mode === 'initial'
      ? String(
          (props.formData as { stage?: string; sysRequestStage?: string })
            .sysRequestStage ??
            (props.formData as { stage?: string }).stage ??
            '',
        )
      : String(
          (props.request as { sysRequestStage?: string; stage?: string })
            .sysRequestStage ??
            (props.request as { stage?: string }).stage ??
            '',
        );
  const requestIdForVisibility =
    props.mode === 'initial' ? props.requestId : props.request.id;
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails,
    requestStage: requestStageForVisibility,
    requestId: requestIdForVisibility,
  });

  useUOM();

  const attachmentBundle = {
    attachmentsBySection,
    onAttachFiles,
    onDeleteAttachment,
    selectedFiles,
    onFilesSelected,
    offlineMode,
    onCaptionChange,
    pendingCaptionEdits,
  };

  if (props.mode === 'initial') {
    const { formData, updateFormData, requestId, disabled = false } = props;

    const sectionIsEditing = !disabled;
    const { disabled: fieldDisabled, readOnlyMode } =
      requestStepFieldModeProps(sectionIsEditing);
    const fd = formData as Record<string, unknown>;
    const initStr = (key: string): string => {
      const raw = fd[key];
      if (raw === undefined || raw === null) {
        return '';
      }
      return String(raw);
    };
    const requestStageStr = String(
      (fd.sysRequestStage as string | undefined) ??
        (fd.stage as string | undefined) ??
        '',
    );
    const facilityPowerMetrics = subsectionHeaderMetricsProps(
      FACILITY_POWER_SUBSECTION_FIELDS,
      fd,
      fieldVisibility,
    );

    return (
      <>
        {visibleSections.has('readiness') && !omitMainFacilityPowerCard ? (
          <SubsectionForm
            title="Facility Power"
            {...facilityPowerMetrics}
            titleSubtitle={FACILITY_POWER_SECTION_SUBTITLE}
            sectionId={SECTION_IDS.FACILITY_POWER}
            requestId={requestId}
            allowAttachments={true}
            attachments={attachmentsBySection[SECTION_IDS.FACILITY_POWER] || []}
            onAttachFiles={() => onAttachFiles?.(SECTION_IDS.FACILITY_POWER)}
            onDeleteAttachment={onDeleteAttachment}
            isEditing={sectionIsEditing}
            disabled={fieldDisabled}
            readOnlyMode={readOnlyMode}
            selectedFiles={selectedFiles[SECTION_IDS.FACILITY_POWER] || []}
            onFilesSelected={
              onFilesSelected
                ? files => onFilesSelected(SECTION_IDS.FACILITY_POWER, files)
                : undefined
            }
            isOffline={offlineMode}
          >
            <FormFieldStack>
              <FacilityPowerReadinessPermissionField
                fieldId="powerMainNominalLvVoltage"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
                formDisabled={fieldDisabled}
                fieldVisibility={fieldVisibility}
              >
                <FormDropdown
                  label="Main nominal low voltage"
                  value={initStr('powerMainNominalLvVoltage')}
                  options={voltageOptions}
                  onSelect={value =>
                    updateFormData('powerMainNominalLvVoltage', String(value))
                  }
                  placeholder="Select voltage"
                  disabled={fieldDisabled}
                  readOnlyMode={readOnlyMode}
                />
              </FacilityPowerReadinessPermissionField>
              <FacilityPowerReadinessPermissionField
                fieldId="powerAnticipatedFinalMwSize"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
                formDisabled={fieldDisabled}
                fieldVisibility={fieldVisibility}
              >
                <NumberInput
                  label="Anticipated IT Load MW"
                  value={initStr('powerAnticipatedFinalMwSize')}
                  onChangeText={value =>
                    updateFormData('powerAnticipatedFinalMwSize', value)
                  }
                  placeholder="Enter anticipated future IT load in MW"
                  min={0}
                  showStepper={false}
                  disabled={fieldDisabled}
                  readOnlyMode={readOnlyMode}
                />
              </FacilityPowerReadinessPermissionField>
              <FacilityPowerReadinessPermissionField
                fieldId="powerFacilityDescription"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
                formDisabled={fieldDisabled}
                fieldVisibility={fieldVisibility}
              >
                <FormInput
                  label="Description"
                  value={initStr('powerFacilityDescription')}
                  onChangeText={value =>
                    updateFormData('powerFacilityDescription', value)
                  }
                  placeholder="Enter description"
                  multiline
                  maxLength={10000}
                  richText
                  requestId={requestId}
                  requestSection={SECTION_IDS.FACILITY_POWER}
                  isOnline={!offlineMode}
                  disabled={fieldDisabled}
                  readOnlyMode={readOnlyMode}
                />
              </FacilityPowerReadinessPermissionField>
              <FacilityPowerReadinessPermissionField
                fieldId="powerMainNominalAmps"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
                formDisabled={fieldDisabled}
                fieldVisibility={fieldVisibility}
              >
                <NumberInput
                  label="Main nominal amps"
                  value={initStr('powerMainNominalAmps')}
                  onChangeText={value =>
                    updateFormData('powerMainNominalAmps', value)
                  }
                  placeholder="Enter main nominal amperage capacity"
                  min={0}
                  step={0.01}
                  showStepper={false}
                  disabled={fieldDisabled}
                  readOnlyMode={readOnlyMode}
                />
              </FacilityPowerReadinessPermissionField>
              <FacilityPowerReadinessPermissionField
                fieldId="powerTotalAvailablePowerKw"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
                formDisabled={fieldDisabled}
                fieldVisibility={fieldVisibility}
              >
                <NumberInput
                  label="Total available power in kW"
                  value={initStr('powerTotalAvailablePowerKw')}
                  onChangeText={value =>
                    updateFormData('powerTotalAvailablePowerKw', value)
                  }
                  placeholder="Enter total available power"
                  min={0}
                  showStepper={false}
                  disabled={fieldDisabled}
                  readOnlyMode={readOnlyMode}
                />
              </FacilityPowerReadinessPermissionField>
              <FacilityPowerReadinessPermissionField
                fieldId="powerTotalDataCenterItLoadKw"
                requestStage={requestStageStr}
                requestId={requestId}
                showFullDetails={showFullDetails}
                formDisabled={fieldDisabled}
                fieldVisibility={fieldVisibility}
              >
                <NumberInput
                  label="Total data center IT load in kW"
                  value={initStr('powerTotalDataCenterItLoadKw')}
                  onChangeText={value =>
                    updateFormData('powerTotalDataCenterItLoadKw', value)
                  }
                  placeholder="Enter total IT load"
                  min={0}
                  showStepper={false}
                  disabled={fieldDisabled}
                  readOnlyMode={readOnlyMode}
                />
              </FacilityPowerReadinessPermissionField>
            </FormFieldStack>
          </SubsectionForm>
        ) : null}

        {visibleSections.has('large-ups') ? (
          <LargeUpsInformation
            mode="initial"
            formData={formData}
            updateFormData={updateFormData}
            requestId={requestId}
            disabled={disabled}
            isEditing={sectionIsEditing}
            showFullDetails={showFullDetails}
            {...attachmentBundle}
          />
        ) : null}

        {visibleSections.has('power-distribution') &&
        shouldShowPowerDistributionSection({ stage: requestStageStr }) ? (
          <PowerDistributionInformation
            mode="initial"
            formData={formData}
            updateFormData={updateFormData}
            requestId={requestId}
            disabled={disabled}
            isEditing={sectionIsEditing}
            showFullDetails={showFullDetails}
            {...attachmentBundle}
          />
        ) : null}
      </>
    );
  }

  const { request, isEditing, editedData, updateEditedData } = props;
  const { disabled: fieldDisabled, readOnlyMode } =
    requestStepFieldModeProps(isEditing);

  const stageRaw = request.stage ?? request.sysRequestStage;
  const permissionStage = Array.isArray(stageRaw)
    ? stageRaw[0] != null
      ? String(stageRaw[0])
      : ''
    : stageRaw != null
    ? String(stageRaw)
    : '';

  const getValue = (field: string) => {
    const fromRequest = (request as unknown as Record<string, unknown>)[field];
    const raw = isEditing ? editedData[field] ?? fromRequest : fromRequest;
    if (raw === undefined || raw === null) {
      return '';
    }
    return String(raw);
  };

  const headerValues = {
    ...(request as unknown as Record<string, unknown>),
    ...(isEditing ? editedData : {}),
  };
  const facilityPowerMetrics = subsectionHeaderMetricsProps(
    FACILITY_POWER_SUBSECTION_FIELDS,
    headerValues,
    fieldVisibility,
  );

  return (
    <>
      {visibleSections.has('readiness') && !omitMainFacilityPowerCard ? (
        <SubsectionForm
          title="Facility Power"
          {...facilityPowerMetrics}
          titleSubtitle={FACILITY_POWER_SECTION_SUBTITLE}
          sectionId={SECTION_IDS.FACILITY_POWER}
          requestId={request.id}
          allowAttachments={true}
          attachments={attachmentsBySection[SECTION_IDS.FACILITY_POWER] || []}
          onAttachFiles={() => onAttachFiles?.(SECTION_IDS.FACILITY_POWER)}
          onDeleteAttachment={onDeleteAttachment}
          isEditing={isEditing}
          disabled={fieldDisabled}
          readOnlyMode={readOnlyMode}
          selectedFiles={selectedFiles[SECTION_IDS.FACILITY_POWER] || []}
          onFilesSelected={
            onFilesSelected
              ? files => onFilesSelected(SECTION_IDS.FACILITY_POWER, files)
              : undefined
          }
          isOffline={offlineMode}
          onCaptionChange={onCaptionChange}
          pendingCaptionEdits={pendingCaptionEdits}
        >
          <FormFieldStack>
            <FacilityPowerReadinessPermissionField
              fieldId="powerMainNominalLvVoltage"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
              formDisabled={fieldDisabled}
              fieldVisibility={fieldVisibility}
            >
              <FormDropdown
                label="Main nominal low voltage"
                value={String(getValue('powerMainNominalLvVoltage') ?? '')}
                options={voltageOptions}
                onSelect={
                  isEditing
                    ? value =>
                        updateEditedData(
                          'powerMainNominalLvVoltage',
                          String(value),
                        )
                    : () => {}
                }
                placeholder="Select voltage"
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </FacilityPowerReadinessPermissionField>
            <FacilityPowerReadinessPermissionField
              fieldId="powerAnticipatedFinalMwSize"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
              formDisabled={fieldDisabled}
              fieldVisibility={fieldVisibility}
            >
              <NumberInput
                label="Anticipated IT Load MW"
                value={getValue('powerAnticipatedFinalMwSize')}
                onChangeText={
                  isEditing
                    ? value =>
                        updateEditedData('powerAnticipatedFinalMwSize', value)
                    : () => {}
                }
                placeholder="Enter anticipated future IT load in MW"
                min={0}
                showStepper={false}
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </FacilityPowerReadinessPermissionField>
            <FacilityPowerReadinessPermissionField
              fieldId="powerFacilityDescription"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
              formDisabled={fieldDisabled}
              fieldVisibility={fieldVisibility}
            >
              <FormInput
                label="Description"
                value={getValue('powerFacilityDescription')}
                onChangeText={
                  isEditing
                    ? value =>
                        updateEditedData('powerFacilityDescription', value)
                    : () => {}
                }
                placeholder="Enter description"
                multiline
                maxLength={10000}
                richText
                requestId={String(request.id)}
                requestSection={SECTION_IDS.FACILITY_POWER}
                isOnline={!offlineMode}
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </FacilityPowerReadinessPermissionField>
            <FacilityPowerReadinessPermissionField
              fieldId="powerMainNominalAmps"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
              formDisabled={fieldDisabled}
              fieldVisibility={fieldVisibility}
            >
              <NumberInput
                label="Main nominal amps"
                value={getValue('powerMainNominalAmps')}
                onChangeText={
                  isEditing
                    ? value => updateEditedData('powerMainNominalAmps', value)
                    : () => {}
                }
                placeholder="Enter main nominal amperage capacity"
                min={0}
                step={0.01}
                showStepper={false}
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </FacilityPowerReadinessPermissionField>
            <FacilityPowerReadinessPermissionField
              fieldId="powerTotalAvailablePowerKw"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
              formDisabled={fieldDisabled}
              fieldVisibility={fieldVisibility}
            >
              <NumberInput
                label="Total available power in kW"
                value={getValue('powerTotalAvailablePowerKw')}
                onChangeText={
                  isEditing
                    ? value =>
                        updateEditedData('powerTotalAvailablePowerKw', value)
                    : () => {}
                }
                placeholder="Enter total available power"
                min={0}
                showStepper={false}
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </FacilityPowerReadinessPermissionField>
            <FacilityPowerReadinessPermissionField
              fieldId="powerTotalDataCenterItLoadKw"
              requestStage={permissionStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
              formDisabled={fieldDisabled}
              fieldVisibility={fieldVisibility}
            >
              <NumberInput
                label="Total data center IT load in kW"
                value={getValue('powerTotalDataCenterItLoadKw')}
                onChangeText={
                  isEditing
                    ? value =>
                        updateEditedData('powerTotalDataCenterItLoadKw', value)
                    : () => {}
                }
                placeholder="Enter total IT load"
                min={0}
                showStepper={false}
                disabled={fieldDisabled}
                readOnlyMode={readOnlyMode}
              />
            </FacilityPowerReadinessPermissionField>
          </FormFieldStack>
        </SubsectionForm>
      ) : null}

      {visibleSections.has('large-ups') ? (
        <LargeUpsInformation
          mode="persisted"
          request={request}
          isEditing={isEditing}
          editedData={editedData}
          showFullDetails={showFullDetails}
          getValue={getValue}
          updateEditedData={updateEditedData}
          {...attachmentBundle}
        />
      ) : null}

      {visibleSections.has('power-distribution') &&
      shouldShowPowerDistributionSection({ stage: permissionStage }) ? (
        <PowerDistributionInformation
          mode="persisted"
          request={request}
          isEditing={isEditing}
          editedData={editedData}
          showFullDetails={showFullDetails}
          getValue={getValue}
          updateEditedData={updateEditedData}
          {...attachmentBundle}
        />
      ) : null}
    </>
  );
};

FacilityPowerReadiness.displayName = 'FacilityPowerReadiness';
